import { CSSProperties, FC, useId, useState } from 'react';

interface MiDashLogoProps {
  size?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
  animateOnHover?: boolean;
  alwaysAnimate?: boolean;
  emphasis?: 'normal' | 'high';
}

const MiDashLogo: FC<MiDashLogoProps> = ({
  size,
  height,
  className = '',
  style,
  animateOnHover = false,
  alwaysAnimate = false,
  emphasis = 'normal',
}) => {
  const finalSize = height || size || 48;
  const rawId = useId().replace(/:/g, '-');
  const [isHovered, setIsHovered] = useState(false);
  const litGradientId = `${rawId}-lit-gradient`;
  const dimGradientId = `${rawId}-dim-gradient`;
  const centerGradientId = `${rawId}-center-gradient`;
  const shadowId = `${rawId}-shadow`;
  const centerClass = `${rawId}-center-circle`;
  const wrapperClass = `${rawId}-wrapper`;

  // Dot positions with timing offsets for wave animation
  const dots: [number, number, boolean, number][] = [
    [280, 280, true, 0],
    [420, 280, false, 0.15],
    [580, 280, true, 0.3],
    [720, 280, false, 0.45],
    [280, 420, false, 1.65],
    [720, 420, true, 0.6],
    [280, 580, true, 1.5],
    [720, 580, false, 0.75],
    [280, 720, false, 1.35],
    [420, 720, true, 1.2],
    [580, 720, false, 1.05],
    [720, 720, true, 0.9],
  ];

  const shouldAnimate = alwaysAnimate || (animateOnHover && isHovered);
  const forceLit = emphasis === 'high';

  return (
    <div
      className={`relative ${className} ${wrapperClass}`}
      style={{
        width: finalSize,
        height: finalSize,
        ...style,
      }}
      onMouseEnter={() => animateOnHover && setIsHovered(true)}
      onMouseLeave={() => animateOnHover && setIsHovered(false)}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="200 200 600 600"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="presentation"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={litGradientId} cx="30%" cy="30%">
            <stop offset="0%" stopColor="#6dd8d0" stopOpacity="1" />
            <stop offset="30%" stopColor="#5bc9c1" stopOpacity="1" />
            <stop offset="70%" stopColor="#4db8b0" stopOpacity="1" />
            <stop offset="100%" stopColor="#3f9da1" stopOpacity="1" />
          </radialGradient>

          <radialGradient id={dimGradientId} cx="30%" cy="30%">
            <stop offset="0%" stopColor="#6dd8d0" stopOpacity="0.8" />
            <stop offset="30%" stopColor="#5bc9c1" stopOpacity="0.75" />
            <stop offset="70%" stopColor="#4db8b0" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#3f9da1" stopOpacity="0.65" />
          </radialGradient>

          <radialGradient id={centerGradientId} cx="28%" cy="28%">
            <stop offset="0%" stopColor="#6dd8d0" stopOpacity="1" />
            <stop offset="25%" stopColor="#5bc9c1" stopOpacity="1" />
            <stop offset="65%" stopColor="#4db8b0" stopOpacity="1" />
            <stop offset="100%" stopColor="#3f9da1" stopOpacity="1" />
          </radialGradient>

          <filter id={shadowId}>
            <feDropShadow dx="3" dy="5" stdDeviation="4" floodColor="#3f9da1" floodOpacity="0.5" />
          </filter>
        </defs>

        {dots.map(([cx, cy, lit, delay], idx) => (
          <circle
            key={`${cx}-${cy}-${idx}`}
            cx={cx}
            cy={cy}
            r="50"
            fill={`url(#${lit || forceLit ? litGradientId : dimGradientId})`}
            filter={`url(#${shadowId})`}
          >
            {shouldAnimate && (
              <animate
                attributeName="opacity"
                values={lit || forceLit ? "1;0.4;1" : "0.7;1;0.7"}
                dur="2s"
                begin={`${delay}s`}
                repeatCount="indefinite"
              />
            )}
          </circle>
        ))}

        <circle
          cx="500"
          cy="500"
          r="75"
          fill={`url(#${centerGradientId})`}
          filter={`url(#${shadowId})`}
          className={centerClass}
        >
          {shouldAnimate && (
            <>
              <animate attributeName="opacity" values="1;0.7;1" dur="2s" begin="0s" repeatCount="indefinite" />
              <animate attributeName="r" values="75;65;75" dur="2s" begin="0s" repeatCount="indefinite" />
            </>
          )}
        </circle>

        <style>{`
          .${centerClass} {
            transform-origin: 500px 500px;
            animation: ${rawId}-center-pulse 2.6s ease-in-out infinite;
          }

          @keyframes ${rawId}-center-pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            60% {
              transform: scale(1.08);
              opacity: 0.9;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </svg>
    </div>
  );
};

export default MiDashLogo;
